<template>
  <header class="top-nav">
    <div class="top-nav-inner">
      <router-link to="/dashboard" class="top-nav-brand">
        <img src="@/assets/logo-alfabit.svg" alt="AlfaBit" class="brand-icon-img theme-logo" />
      </router-link>

      <nav class="top-nav-links" v-if="!isMobile" :aria-label="t('a11y.mainNavigation')">
        <template v-for="link in navLinks" :key="link.to">
          <button
            v-if="link.locked"
            type="button"
            class="nav-link"
            :title="verificationRequiredHint"
            @click="goToVerification"
          >{{ link.label }}</button>
          <ServicesMegaMenu
            v-else-if="link.mega"
            :to="link.to"
            :label="link.label"
            :active="isActive(link.to)"
          />
          <router-link
            v-else
            :to="link.to"
            class="nav-link"
            :class="{ active: isActive(link.to) }"
          >{{ link.label }}</router-link>
        </template>
      </nav>

      <div class="top-nav-actions">
        <AccessibilityMenu />
        <ThemeSwitcher />
        <LanguageSwitcher />

        <div class="user-avatar-wrapper" ref="avatarRef">
          <button
            type="button"
            class="user-avatar-btn"
            @click="showUserMenu = !showUserMenu"
            :aria-expanded="showUserMenu"
            aria-haspopup="menu"
            :aria-label="t('navigation.profile')"
          >
            <span class="avatar-circle">{{ userStore.initials }}</span>
          </button>
          <Teleport to="body" :disabled="!isMobile">
            <transition name="fade">
              <div v-if="showUserMenu && isMobile" class="user-dropdown-overlay" @click="showUserMenu = false"></div>
            </transition>
            <transition name="dropdown">
              <div v-if="showUserMenu" ref="dropdownRef" class="user-dropdown">
                <div class="dropdown-user-info">
                  <div class="dropdown-avatar">{{ userStore.initials }}</div>
                  <div class="dropdown-user-details">
                    <span class="dropdown-user-name">{{ userStore.fullName }}</span>
                    <span class="dropdown-user-email">{{ userStore.user?.email }}</span>
                  </div>
                </div>
                <div class="dropdown-divider"></div>
                <router-link to="/settings" class="dropdown-item" @click="showUserMenu = false">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c.26.604.852.997 1.51 1H21a2 2 0 0 1 0 4h-.09c-.658.003-1.25.396-1.51 1z"/></svg>
                  <span>{{ t('sidebar.settings') }}</span>
                </router-link>
                <router-link to="/source-of-funds" class="dropdown-item" @click="showUserMenu = false">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  <span>Источник средств</span>
                </router-link>
                <router-link to="/verification" class="dropdown-item" @click="showUserMenu = false">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                  <span>{{ t('sidebar.verification') }}</span>
                </router-link>
                <router-link v-if="canAccessTeamSection" to="/team" class="dropdown-item" @click="showUserMenu = false">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                  <span>{{ t('sidebar.myTeam') }}</span>
                </router-link>
                <router-link to="/limits-tariffs" class="dropdown-item" @click="showUserMenu = false">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                  <span>{{ t('sidebar.limitsTariffs') }}</span>
                </router-link>
                <router-link to="/legal" class="dropdown-item" @click="showUserMenu = false">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  <span>{{ t('sidebar.legal') }}</span>
                </router-link>
                <router-link to="/help" class="dropdown-item" @click="showUserMenu = false">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                  <span>{{ t('sidebar.help') }}</span>
                </router-link>
                <div class="dropdown-divider"></div>
                <button class="dropdown-item logout" @click="logout">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                  <span>{{ t('sidebar.logout') }}</span>
                </button>
              </div>
            </transition>
          </Teleport>
        </div>
      </div>
    </div>
  </header>

  <!-- Mobile bottom nav -->
  <nav v-if="isMobile" class="mobile-bottom-nav" :aria-label="t('a11y.mainNavigation')">
    <router-link to="/dashboard" class="mob-nav-item" :class="{ active: $route.path === '/dashboard' }">
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
      <span>{{ t('sidebar.dashboard') }}</span>
    </router-link>
    <router-link to="/accounts" class="mob-nav-item" :class="{ active: $route.path === '/accounts' }">
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
      <span>{{ t('sidebar.accounts') }}</span>
    </router-link>
    <button
      v-if="!userStore.isIdentityVerified"
      type="button"
      class="mob-nav-item"
      :title="verificationRequiredHint"
      @click="goToVerification"
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
      <span>{{ t('sidebar.servicePayments') }}</span>
    </button>
    <router-link
      v-else
      to="/payments"
      class="mob-nav-item"
      :class="{ active: $route.path === '/payments' }"
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
      <span>{{ t('sidebar.servicePayments') }}</span>
    </router-link>
    <button
      v-if="!userStore.isIdentityVerified"
      type="button"
      class="mob-nav-item"
      :title="verificationRequiredHint"
      @click="goToVerification"
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="17 2 21 6 17 10"/><path d="M3 6h18"/><polyline points="7 14 3 18 7 22"/><path d="M21 18H3"/></svg>
      <span>{{ t('sidebar.transfers') }}</span>
    </button>
    <router-link
      v-else
      to="/transfer"
      class="mob-nav-item"
      :class="{ active: $route.path === '/transfer' }"
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="17 2 21 6 17 10"/><path d="M3 6h18"/><polyline points="7 14 3 18 7 22"/><path d="M21 18H3"/></svg>
      <span>{{ t('sidebar.transfers') }}</span>
    </router-link>
    <button class="mob-nav-item" @click="showMobileMore = !showMobileMore" :class="{ active: showMobileMore }">
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/></svg>
      <span>{{ t('sidebar.more') }}</span>
    </button>
  </nav>

  <!-- Mobile more sheet -->
  <transition name="fade">
    <div v-if="isMobile && showMobileMore" class="mobile-more-overlay" @click="showMobileMore = false">
      <div class="mobile-more-sheet" @click.stop>
        <div class="sheet-handle"></div>
        <button
          v-if="!userStore.isIdentityVerified"
          type="button"
          class="sheet-item"
          :title="verificationRequiredHint"
          @click="goToVerification"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2v20"/><path d="m5 12 7 7 7-7"/></svg>
          <span>{{ t('sidebar.deposits') }}</span>
        </button>
        <router-link v-else to="/deposit" class="sheet-item" @click="showMobileMore = false">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2v20"/><path d="m5 12 7 7 7-7"/></svg>
          <span>{{ t('sidebar.deposits') }}</span>
        </router-link>
        <router-link to="/transactions" class="sheet-item" @click="showMobileMore = false">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          <span>{{ t('sidebar.transactions') }}</span>
        </router-link>
        <router-link to="/settings" class="sheet-item" @click="showMobileMore = false">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09"/></svg>
          <span>{{ t('sidebar.settings') }}</span>
        </router-link>
        <router-link to="/help" class="sheet-item" @click="showMobileMore = false">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          <span>{{ t('sidebar.help') }}</span>
        </router-link>
        <div class="sheet-divider"></div>
        <button class="sheet-item sheet-logout" @click="showMobileMore = false; logout()">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
          <span>{{ t('sidebar.logout') }}</span>
        </button>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { useI18n } from '@/composables/useI18n'
import { useIsMobile } from '@/composables/useIsMobile'
import LanguageSwitcher from '@/components/LanguageSwitcher.vue'
import AccessibilityMenu from '@/components/AccessibilityMenu.vue'
import ThemeSwitcher from '@/components/ThemeSwitcher.vue'
import ServicesMegaMenu from '@/components/ServicesMegaMenu.vue'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const userStore = useUserStore()

const showUserMenu = ref(false)
const showMobileMore = ref(false)
const avatarRef = ref<HTMLElement | null>(null)
const dropdownRef = ref<HTMLElement | null>(null)
// 48rem ≈ 768px at 100%; grows with a11y font scale so desktop nav collapses earlier
const isMobile = useIsMobile(48)

const canAccessTeamSection = computed(() => {
  return userStore.user?.user_type === 'legal_entity'
})

const verificationRequiredHint = computed(() =>
  t('dashboard.kycWalletAction', 'Пройти верификацию')
)

const navLinks = computed(() => {
  const locked = !userStore.isIdentityVerified
  return [
    { to: '/dashboard', label: t('sidebar.dashboard'), locked: false, mega: false },
    { to: '/accounts', label: t('sidebar.accounts'), locked: false, mega: false },
    { to: '/deposit', label: t('sidebar.deposits'), locked, mega: false },
    { to: '/transfer', label: t('sidebar.transfers'), locked, mega: false },
    { to: '/payments', label: t('sidebar.servicePayments'), locked, mega: true },
    { to: '/transactions', label: t('sidebar.transactions'), locked: false, mega: false },
  ]
})

function goToVerification() {
  showUserMenu.value = false
  showMobileMore.value = false
  router.push('/verification')
}

function isActive(path: string) {
  return route.path === path || route.path.startsWith(path + '/')
}

function handleClickOutside(e: MouseEvent) {
  const target = e.target as Node
  if (
    avatarRef.value &&
    !avatarRef.value.contains(target) &&
    !dropdownRef.value?.contains(target)
  ) {
    showUserMenu.value = false
  }
}

async function logout() {
  showUserMenu.value = false
  await userStore.logout()
  router.push('/login')
}

watch(() => route.path, () => {
  showUserMenu.value = false
  showMobileMore.value = false
})

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.top-nav {
  position: sticky;
  top: 0;
  z-index: 100;
  background: rgba(11, 14, 17, 0.88);
  border-bottom: 1px solid var(--border-primary);
  backdrop-filter: blur(16px);
  overflow: visible;
}

[data-theme="light"] .top-nav {
  background: rgba(255, 255, 255, 0.9);
  border-bottom: 1px solid var(--border-primary);
}

.top-nav-inner {
  max-width: 1280px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  min-height: 4rem;
  height: auto;
  padding: 0.5rem 2rem;
  gap: 1rem 2rem;
  flex-wrap: wrap;
}

.top-nav-brand {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  text-decoration: none;
  flex-shrink: 0;
}

.brand-icon-img {
  width: 96px;
  height: auto;
  object-fit: contain;
}

/* Nav links */
.top-nav-links {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem;
  flex: 1 1 12rem;
  min-width: 0;
  justify-content: center;
  overflow: visible;
}

.top-nav-links :deep(.nav-link) {
  padding: 0.5rem 0.875rem;
  font-size: 0.9375rem;
  font-weight: 500;
  font-family: inherit;
  color: var(--text-secondary);
  text-decoration: none;
  border: none;
  background: none;
  border-radius: var(--radius-md);
  transition: color 0.2s ease, background 0.2s ease;
  white-space: nowrap;
  cursor: pointer;
}

.top-nav-links :deep(.nav-link:hover),
.top-nav-links :deep(.nav-link.open) {
  color: var(--text-primary);
  background: var(--background-tertiary);
}

.top-nav-links :deep(.nav-link.active) {
  color: var(--color-brand);
  font-weight: 700;
  background: var(--color-brand-soft);
}

[data-theme="light"] .top-nav-links :deep(.nav-link:hover),
[data-theme="light"] .top-nav-links :deep(.nav-link.open) {
  background: var(--background-tertiary);
}

[data-theme="light"] .top-nav-links :deep(.nav-link.active) {
  color: var(--color-brand-deep);
}

/* Actions */
.top-nav-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
}

/* User avatar */
.user-avatar-wrapper {
  position: relative;
}

.user-avatar-btn {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
}

.avatar-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--gradient-primary);
  color: #0c2819;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.875rem;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.user-avatar-btn:hover .avatar-circle {
  box-shadow: 0 0 0 3px rgba(84, 215, 134, 0.28);
  transform: scale(1.05);
}

/* Mobile dropdown overlay */
.user-dropdown-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 199;
}

/* Dropdown menu */
.user-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 280px;
  background: var(--background-secondary);
  border: 1px solid var(--border-primary);
  border-radius: var(--radius-lg);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  z-index: 200;
}

[data-theme="light"] .user-dropdown {
  background: #fff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
}

.dropdown-user-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
}

.dropdown-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: var(--gradient-primary);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1rem;
  flex-shrink: 0;
}

.dropdown-user-details {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.dropdown-user-name {
  font-weight: 600;
  font-size: 0.9375rem;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dropdown-user-email {
  font-size: 0.8125rem;
  color: var(--text-tertiary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dropdown-divider {
  height: 1px;
  background: var(--border-primary);
  margin: 0;
}

[data-theme="light"] .dropdown-divider {
  background: #e5e7eb;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-secondary);
  text-decoration: none;
  transition: all 0.15s ease;
  border: none;
  background: none;
  width: 100%;
  cursor: pointer;
  text-align: left;
}

.dropdown-item:hover {
  background: var(--background-tertiary);
  color: var(--text-primary);
}

[data-theme="light"] .dropdown-item:hover {
  background: #f1f5f9;
}

.dropdown-item.logout {
  color: var(--primary-red, #ef4444);
}

.dropdown-item.logout:hover {
  background: rgba(239, 68, 68, 0.08);
  color: var(--primary-red, #ef4444);
}

.dropdown-item svg {
  flex-shrink: 0;
  opacity: 0.7;
}

.dropdown-item:hover svg {
  opacity: 1;
}

/* Dropdown transitions */
.dropdown-enter-active {
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}
.dropdown-leave-active {
  transition: all 0.15s ease-in;
}
.dropdown-enter-from {
  opacity: 0;
  transform: translateY(-8px) scale(0.96);
}
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px) scale(0.98);
}

/* Mobile bottom nav */
.mobile-bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background: rgba(11, 14, 17, 0.94);
  border-top: 1px solid var(--border-primary);
  display: flex;
  align-items: stretch;
  min-height: 4.25rem;
  height: auto;
  padding: 0.375rem 0.25rem calc(0.375rem + env(safe-area-inset-bottom, 0));
  backdrop-filter: blur(16px);
}

[data-theme="light"] .mobile-bottom-nav {
  background: rgba(255, 255, 255, 0.96);
  border-top: 1px solid var(--border-primary);
}

.mob-nav-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 0.625rem;
  font-weight: 500;
  color: var(--text-tertiary);
  text-decoration: none;
  background: none;
  border: none;
  cursor: pointer;
  transition: color 0.2s ease;
  padding: 0;
  min-width: 0;
  line-height: 1.2;
  text-align: center;
  -webkit-tap-highlight-color: transparent;
}

.mob-nav-item span {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  width: 100%;
  text-align: center;
  overflow: hidden;
  line-height: 1.15;
  padding: 0 2px;
  word-break: break-word;
}

.mob-nav-item svg {
  flex-shrink: 0;
  display: block;
  transition: transform 0.2s ease;
}

.mob-nav-item.active {
  color: var(--color-brand, var(--primary-green));
}

.mob-nav-item.active svg {
  transform: scale(1.1);
}

/* Mobile more overlay */
.mobile-more-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 150;
  display: flex;
  align-items: flex-end;
}

.mobile-more-sheet {
  width: 100%;
  background: var(--background-secondary);
  border-radius: var(--radius-xl) var(--radius-xl) 0 0;
  padding: 0.5rem 0 1rem;
  animation: slideUpSheet 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  padding-bottom: calc(1rem + env(safe-area-inset-bottom, 0));
}

[data-theme="light"] .mobile-more-sheet {
  background: #fff;
}

.sheet-handle {
  width: 40px;
  height: 4px;
  background: var(--border-secondary);
  border-radius: 9999px;
  margin: 0.5rem auto 1rem;
}

.sheet-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.875rem 1.5rem;
  color: var(--text-secondary);
  text-decoration: none;
  font-size: 0.9375rem;
  font-family: inherit;
  font-weight: 500;
  transition: background 0.15s ease;
  border: none;
  background: none;
  width: 100%;
  cursor: pointer;
  text-align: left;
}

.sheet-item:hover {
  background: var(--background-tertiary);
  color: var(--text-primary);
}

.sheet-divider {
  height: 1px;
  background: var(--border-primary);
  margin: 0.25rem 1.5rem;
}

[data-theme="light"] .sheet-divider {
  background: #e5e7eb;
}

.sheet-logout {
  color: var(--primary-red, #ef4444) !important;
  border: none;
  background: none;
  width: 100%;
  cursor: pointer;
  text-align: left;
}

.sheet-logout:hover {
  background: rgba(239, 68, 68, 0.08) !important;
  color: var(--primary-red, #ef4444) !important;
}

@keyframes slideUpSheet {
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
}

.fade-enter-active, .fade-leave-active { transition: opacity 0.2s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

/* Responsive — rem breakpoints track a11y root font-size */
@media (max-width: 68.75rem) {
  .top-nav-inner {
    gap: 0.75rem 1rem;
    padding: 0.5rem 1rem;
  }
  .nav-link {
    padding: 0.4rem 0.625rem;
    font-size: 0.875rem;
  }
}

@media (max-width: 48rem) {
  .top-nav-inner {
    min-height: 3.5rem;
    padding: 0.375rem 0.75rem;
    justify-content: space-between;
    flex-wrap: nowrap;
  }
  .top-nav-links {
    display: none;
  }
  .brand-name {
    display: none;
  }
  .top-nav-actions {
    margin-left: auto;
  }

  .user-dropdown {
    position: fixed;
    top: auto;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    max-width: 100%;
    border-radius: var(--radius-xl) var(--radius-xl) 0 0;
    box-shadow: 0 -8px 40px rgba(0, 0, 0, 0.3);
    max-height: 80vh;
    overflow-y: auto;
    padding-bottom: calc(4.25rem + env(safe-area-inset-bottom, 0));
  }

  .dropdown-enter-active {
    transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  }
  .dropdown-leave-active {
    transition: all 0.2s ease-in;
  }
  .dropdown-enter-from {
    opacity: 0;
    transform: translateY(100%);
  }
  .dropdown-leave-to {
    opacity: 0;
    transform: translateY(100%);
  }

  .dropdown-user-info {
    padding: 1.25rem 1.25rem;
  }

  .dropdown-item {
    padding: 0.875rem 1.25rem;
    font-size: 0.9375rem;
  }
}

</style>
